import { signal } from '@preact/signals';
import { AsteroidElements, CometElements, SmallBody, SmallBodyState, parseCometEls, smallBodyState } from '../astro/smallbodies';
import { idbGet, idbSet } from '../lib/idb';
import { Vec3, clamp } from '../lib/math';
import type { FrameContext, PickHit, SkyProvider } from '../render/engine';
import { eyeSeesAt } from '../render/nakedeye';
import type { LabelCandidate } from '../render/overlay';
import { Selection, selection, settings } from '../state';
import type { CloseApproach } from '../../server/api';

export type { CloseApproach };

export const neoList = signal<CloseApproach[] | null>(null);
/** Goes up whenever the comets or asteroids are loaded or refreshed, so lists made from them can be remade. */
export const smallBodiesRev = signal(0);

interface View {
  body: SmallBody;
  st: SmallBodyState;
  enu: Vec3;
  x: number;
  y: number;
  onScreen: boolean;
  tail: { x: number; y: number } | null; // screen direction of the tail
}

const MPC_URL = 'https://www.minorplanetcenter.net/iau/MPCORB/CometEls.txt';

export class SmallBodyService implements SkyProvider {
  comets: CometElements[] = [];
  asteroids: AsteroidElements[] = [];
  views: View[] = [];
  private candidates: SmallBody[] = [];
  private states = new Map<string, SmallBodyState>();
  private statesAt = -1e15;
  private candidatesAt = -1e15;

  async load() {
    const base = import.meta.env.BASE_URL;
    const [ast, snapshot] = await Promise.all([
      fetch(`${base}data/asteroids.json`).then((r) => r.json()).catch(() => []),
      fetch(`${base}data/comets.txt`).then((r) => r.text()).catch(() => ''),
    ]);
    this.asteroids = (ast as Omit<AsteroidElements, 'kind'>[]).map((a) => ({ ...a, kind: 'asteroid' as const }));
    this.comets = parseCometEls(snapshot);
    smallBodiesRev.value++;
    void this.refreshComets().then(() => smallBodiesRev.value++);
  }

  /** Comet orbits change as new observations arrive; refresh from the Minor Planet Center daily. */
  private async refreshComets() {
    if (import.meta.env.DEV) return;
    const cached = await idbGet<{ text: string; at: number }>('mpc:comets');
    if (cached && Date.now() - cached.at < 24 * 3600e3) {
      this.comets = parseCometEls(cached.text);
      return;
    }
    try {
      const r = await fetch(MPC_URL);
      const text = r.ok ? await r.text() : '';
      if (text.length > 50000) {
        this.comets = parseCometEls(text);
        this.candidatesAt = -1e15;
        await idbSet('mpc:comets', { text, at: Date.now() });
      }
    } catch {
      if (cached) this.comets = parseCometEls(cached.text);
    }
  }

  async loadNeo() {
    if (neoList.peek()) return;
    try {
      const r = await fetch(`${import.meta.env.BASE_URL}api/neo`);
      if (r.ok) neoList.value = await r.json();
    } catch {
      /* offline */
    }
  }

  stateOf(name: string): SmallBodyState | null {
    return this.states.get(name) ?? null;
  }

  find(name: string): SmallBody | undefined {
    return this.asteroids.find((a) => a.name === name) ?? this.comets.find((c) => c.name === name);
  }

  /** Brightest comets and asteroids right now, for lists. */
  brightest(date: Date, n = 6): { body: SmallBody; st: SmallBodyState }[] {
    const all: { body: SmallBody; st: SmallBodyState }[] = [];
    for (const b of [...this.asteroids, ...this.comets]) all.push({ body: b, st: smallBodyState(b, date) });
    return all.sort((a, b) => a.st.mag - b.st.mag).slice(0, n);
  }

  update(ctx: FrameContext) {
    const t = ctx.date.getTime();
    // Re-pick candidates (anything plausibly visible in a telescope) every simulated 12 hours.
    if (Math.abs(t - this.candidatesAt) > 12 * 3600e3) {
      this.candidatesAt = t;
      this.candidates = [...this.asteroids];
      for (const c of this.comets) {
        if (c.q > 6) continue;
        const st = smallBodyState(c, ctx.date);
        if (st.mag < 13) this.candidates.push(c);
      }
      this.statesAt = -1e15;
    }
    if (Math.abs(t - this.statesAt) > 60e3) {
      this.statesAt = t;
      for (const b of this.candidates) this.states.set(b.name, smallBodyState(b, ctx.date));
    }
    this.views.length = 0;
    const cam = ctx.camera;
    const sel = selection.peek();
    for (const b of this.candidates) {
      const st = this.states.get(b.name);
      if (!st) continue;
      const isSel = sel?.kind === 'small' && sel.name === b.name;
      // Show what binoculars could reach at this zoom; always show the selected one. In Naked eye, only what the eye
      // could see: a comet's light is spread out, so it needs to be about a magnitude brighter than a star.
      if (!isSel && !ctx.nakedEye && st.mag > ctx.magLimit + 2.5) continue;
      const enu = ctx.frame.toEnu(st.dir);
      if (enu[2] < -0.05 && !isSel) continue;
      if (!isSel && ctx.nakedEye && !eyeSeesAt(st.mag + (b.kind === 'comet' ? 1 : 0), enu[0], enu[1], enu[2], ctx.eyeSky, settings.peek().seeThroughGround)) continue;
      const p = cam.project(enu, { x: 0, y: 0 });
      let tail: View['tail'] = null;
      if (p && b.kind === 'comet') {
        const e2 = ctx.frame.toEnu(st.antiSun);
        const q = cam.project([enu[0] + e2[0] * 0.02, enu[1] + e2[1] * 0.02, enu[2] + e2[2] * 0.02], { x: 0, y: 0 });
        if (q) {
          const dx = q.x - p.x, dy = q.y - p.y, l = Math.hypot(dx, dy) || 1;
          tail = { x: dx / l, y: dy / l };
        }
      }
      this.views.push({ body: b, st, enu, x: p?.x ?? -1e4, y: p?.y ?? -1e4, onScreen: !!p && cam.onScreen(p, 40), tail });
    }
  }

  overlay(g: CanvasRenderingContext2D, ctx: FrameContext) {
    for (const v of this.views) {
      if (!v.onScreen) continue;
      const bright = clamp((ctx.magLimit + 2.5 - v.st.mag) / 6, 0.15, 1);
      if (v.body.kind === 'comet') {
        // Coma and a tail pointing away from the Sun.
        const R = 4 + bright * 6;
        const coma = g.createRadialGradient(v.x, v.y, 0, v.x, v.y, R * 2);
        coma.addColorStop(0, `rgba(210,255,235,${0.9 * bright})`);
        coma.addColorStop(0.35, `rgba(150,230,210,${0.35 * bright})`);
        coma.addColorStop(1, 'rgba(150,230,210,0)');
        g.fillStyle = coma;
        g.beginPath();
        g.arc(v.x, v.y, R * 2, 0, Math.PI * 2);
        g.fill();
        if (v.tail) {
          const L = (20 + 50 * bright) * clamp(1.2 / Math.max(v.st.r, 0.3), 0.3, 2);
          const tx = v.x + v.tail.x * L, ty = v.y + v.tail.y * L;
          const tg = g.createLinearGradient(v.x, v.y, tx, ty);
          tg.addColorStop(0, `rgba(170,240,225,${0.45 * bright})`);
          tg.addColorStop(1, 'rgba(170,240,225,0)');
          g.fillStyle = tg;
          const nx = -v.tail.y, ny = v.tail.x;
          g.beginPath();
          g.moveTo(v.x + nx * R * 0.7, v.y + ny * R * 0.7);
          g.lineTo(tx + nx * R * 2.2, ty + ny * R * 2.2);
          g.lineTo(tx - nx * R * 2.2, ty - ny * R * 2.2);
          g.lineTo(v.x - nx * R * 0.7, v.y - ny * R * 0.7);
          g.closePath();
          g.fill();
        }
      } else {
        g.fillStyle = `rgba(230,220,200,${0.5 + 0.5 * bright})`;
        g.beginPath();
        g.arc(v.x, v.y, 1.6 + bright * 1.4, 0, Math.PI * 2);
        g.fill();
      }
    }
  }

  labels(ctx: FrameContext, out: LabelCandidate[]) {
    const sel = selection.peek();
    for (const v of this.views) {
      if (!v.onScreen) continue;
      const isSel = sel?.kind === 'small' && sel.name === v.body.name;
      if (!isSel && v.body.kind === 'asteroid' && ctx.camera.fovDeg > 40 && !ctx.nakedEye) continue;
      out.push({
        x: v.x, y: v.y, r: 8, text: v.body.name, sub: `mag ${v.st.mag.toFixed(1)}`, style: 'dso',
        priority: isSel ? 2500 : 900 - v.st.mag * 20, sel: { kind: 'small', name: v.body.name },
      });
    }
  }

  pick(_ctx: FrameContext, x: number, y: number): PickHit | null {
    let best: PickHit | null = null;
    for (const v of this.views) {
      if (!v.onScreen) continue;
      const d = Math.hypot(v.x - x, v.y - y);
      if (d < 24 && (!best || d < best.dist)) best = { sel: { kind: 'small', name: v.body.name }, dist: d };
    }
    return best;
  }

  locate(sel: Selection, ctx: FrameContext): { enu: Vec3; label: string } | null {
    if (sel.kind !== 'small') return null;
    const b = this.find(sel.name);
    if (!b) return null;
    const st = this.states.get(b.name) ?? smallBodyState(b, ctx.date);
    return { enu: ctx.frame.toEnu(st.dir), label: b.name };
  }
}
